import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
} from "lucide-react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { schoolService } from "../services/schoolService";
import Sidebar from "../components/layout/Sidebar";
import school from "../assets/school.svg";
import group from "../assets/group2.svg";
import Pending from "../assets/pending.svg";
import ActivateSchoolModal from "../components/modals/ActivateSchoolModal";
import DeactivateSchoolModal from "../components/modals/DeactivateSchoolModal";
import AddSchoolModal from "../components/modals/AddSchoolModal";
import InviteSchoolAdminModal from "../components/modals/InviteSchoolAdminModal";
import Dropdown from "../components/ui/Dropdown";
import Export from "../assets/export.svg";
import Export1 from "../assets/export1.svg";
import DetailSchoolModal from "../components/modals/DetailSchoolModal";
import SchoolLogo from "../components/SchoolLogo";
import {
  TransferOwnershipModal1,
  TransferOwnershipModal2,
  TransferOwnershipModal3,
  TransferOwnershipModal4,
} from "../components/modals/TransferOwnershipModals";

const TABS = ["Active Schools", "Pending Schools"];

// Student app base URL — used to build a clickable Hypepage link in the
// student export. Trailing slash trimmed so we can append /u/<slug> cleanly.
const STUDENT_URL = (import.meta.env.VITE_STUDENT_URL || "").replace(/\/+$/, "");

// Enum → display ("ACTIVE" → "Active"). UserStatus values are single words.
const titleCaseStatus = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");

// Stats cards will be calculated from API data

// `onClick` is optional. When provided the card renders as a <button> so
// it picks up keyboard activation (Enter/Space) and a focus ring for free,
// and we add hover affordances so users discover it's clickable.
const StatCard = ({ icon, label, value, color, onClick }) => {
  const base = `p-6 bg-white rounded-2xl outline outline-1 outline-${color} flex flex-col gap-6 text-left`;
  const interactive = onClick
    ? "cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
    : "";

  const content = (
    <>
      <img src={icon} alt={label} className="w-10 h-10" />
      <div className="flex justify-between items-end">
        <span className="text-gray-900 text-lg font-medium">{label}</span>
        <span className="text-gray-900 text-[40px] font-medium leading-10">
          {value}
        </span>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${base} ${interactive} w-full`}>
        {content}
      </button>
    );
  }

  return <div className={base}>{content}</div>;
};

const SchoolRow = ({ school, onToggleStatus, onViewSchool, onExportStudents, exporting }) => {
  const isActive = school.status === "Active";

  return (
    <tr className="h-16 hover:bg-gray-50 transition-colors">
      <td className="px-6 text-gray-900 text-xs">
        {/* Clickable identity — opens the same detail modal as the Action
            eye icon, so operators can just click the school. */}
        <button
          type="button"
          onClick={() => onViewSchool(school)}
          title="View school details"
          className="flex items-center gap-2.5 text-left text-gray-900 hover:text-blue-600 transition-colors group"
        >
          <SchoolLogo name={school.school} logoUrl={school.logoUrl} />
          <span className="group-hover:underline">{school.school}</span>
        </button>
      </td>
      <td className="px-6 text-gray-900 text-xs">{school.adminName}</td>
      <td className="px-6 text-gray-900 text-xs">{school.title}</td>
      <td className="px-6 text-gray-900 text-xs">{school.email}</td>
      <td className="px-6 text-gray-900 text-xs">{school.dateJoined}</td>
      <td className="px-6 text-gray-900 text-xs">{school.studentCount}</td>
      <td className="px-6">
        <span
          className={`px-2.5 py-0.5 rounded-[10px] text-xs font-medium ${
            isActive
              ? "bg-emerald-100 text-emerald-800"
              : "bg-[#FEF3C7] text-red-800"
          }`}
        >
          {school.status}
        </span>
      </td>
      <td className="px-6">
        <div className="flex items-center gap-3 flex-nowrap">
          <button
            type="button"
            onClick={() => onViewSchool(school)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <Eye size={18} />
          </button>

          <button
            type="button"
            onClick={() => onExportStudents(school)}
            disabled={exporting}
            className={`text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0 ${
              exporting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <img src={Export1} alt="Export" className="w-[20px] h-[20px]" />
          </button>

          {isActive && (
            <button
              type="button"
              onClick={() => onToggleStatus(school)}
              className="flex-shrink-0 px-3 py-2 bg-red-600 hover:bg-red-700 text-red-50 text-xs font-medium rounded-md whitespace-nowrap transition-colors"
            >
              Deactivate
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// Status badge styling per the latest invite's state. Falls back to
// "No invite" when a school was added but no admin has been invited yet.
const PENDING_STATUS_BADGE = {
  PENDING: { label: "Awaiting admin", cls: "bg-[#FEF3C7] text-amber-800" },
  EXPIRED: { label: "Invite expired", cls: "bg-red-100 text-red-800" },
  CANCELLED: { label: "Invite cancelled", cls: "bg-gray-100 text-gray-600" },
};

// A "pending school" is a real School row with no accepted admin yet
// (schoolAdminId null on the server). Primary action label flips based on
// Open school details for the full staff roster and invitation history.
const PendingSchoolRow = ({ school, onViewSchool, onResend, onCancel, selected, onToggleSelect }) => {
  const badge =
    PENDING_STATUS_BADGE[school.inviteStatus] || {
      label: "No invite",
      cls: "bg-gray-100 text-gray-600",
    };
  const hasInvite = Boolean(school.inviteId);
  const canCancel = hasInvite && school.inviteStatus !== "CANCELLED";

  return (
    <tr className="h-16 hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(school.id)}
          aria-label={`Select ${school.school}`}
          className="w-4 h-4 cursor-pointer accent-primary"
        />
      </td>
      <td className="px-6 py-4 text-gray-900 text-xs">
        <div className="flex items-center gap-2.5">
          <SchoolLogo name={school.school} logoUrl={school.logoUrl} />
          <button type="button" onClick={() => onViewSchool(school)} className="text-left text-blue-600 hover:underline">{school.school}</button>
        </div>
      </td>
      <td className="px-6 py-4 text-xs">
        {school.adminEmail ? (
          <span className="text-gray-900">{school.adminEmail}</span>
        ) : (
          <span className="text-gray-400">No invite sent</span>
        )}
      </td>
      <td className="px-6 py-4 text-gray-900 text-xs">{school.dateAdded}</td>
      <td className="px-6 py-4 text-gray-900 text-xs">{school.studentCount}</td>
      <td className="px-6 py-4">
        <span className={`px-2.5 py-0.5 rounded-[10px] text-xs font-medium ${badge.cls}`}>
          {badge.label}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onViewSchool(school)}
            className="px-3 py-2 bg-primary hover:bg-blue-600 text-white text-xs font-medium rounded-md transition-colors whitespace-nowrap"
          >
            Admins & invitations
          </button>
          {hasInvite && (
            <button
              type="button"
              onClick={() => onResend(school)}
              className="px-3 py-2 border border-blue-500 text-blue-600 hover:bg-blue-50 text-xs font-medium rounded-md transition-colors whitespace-nowrap"
            >
              Resend
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              onClick={() => onCancel(school)}
              className="px-3 py-2 border border-red-300 text-red-600 hover:bg-red-50 text-xs font-medium rounded-md transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const SortButton = ({ columnKey, label, sortConfig, onSort }) => {
  return (
    <div className="flex items-center gap-[10px]">
      <div className="flex flex-col cursor-pointer gap-[2px]">
        <button
          type="button"
          onClick={() => onSort(columnKey, "asc")}
          className="leading-none hover:opacity-80"
        >
          <svg
            width="10"
            height="6"
            viewBox="0 0 12 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`${
              sortConfig.key === columnKey && sortConfig.direction === "asc"
                ? "opacity-100"
                : "opacity-70"
            }`}
          >
            <title>{`Sort ${label} ascending`}</title>
            <path d="M6 0L11.1962 7.5H0.803848L6 0Z" fill="currentColor" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => onSort(columnKey, "desc")}
          className="leading-none hover:opacity-80"
        >
          <svg
            width="10"
            height="6"
            viewBox="0 0 12 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`${
              sortConfig.key === columnKey && sortConfig.direction === "desc"
                ? "opacity-100"
                : "opacity-70"
            }`}
          >
            <title>{`Sort ${label} descending`}</title>
            <path d="M6 8L0.803848 0.5H11.1962L6 8Z" fill="currentColor" />
          </svg>
        </button>
      </div>
      {label}
    </div>
  );
};

const ManageSchools = () => {
  const navigate = useNavigate();
  // Scroll target for the Active/Pending stat-card clicks — anchored at the
  // tab bar so both the tab strip and the loaded table land in view.
  const tableSectionRef = useRef(null);
  const [activeTab, setActiveTab] = useState("Active Schools");
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showAddSchoolModal, setShowAddSchoolModal] = useState(false);
  const [showInviteAdminModal, setShowInviteAdminModal] = useState(false);
  const [selectedSchoolForInvite, setSelectedSchoolForInvite] = useState(null);
  // Non-null when the modal should open in View/Edit mode for an existing
  // invite (Pending row with school.inviteId set). Null = fresh invite.
  const [selectedInviteId, setSelectedInviteId] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [schools, setSchools] = useState([]);
  const [pendingSchools, setPendingSchools] = useState([]);
  const [selectedPendingIds, setSelectedPendingIds] = useState([]);
  const [deletingSchools, setDeletingSchools] = useState(false);
  const [activeStatusFilter, setActiveStatusFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSchoolForDetail, setSelectedSchoolForDetail] = useState(null);
  const [transferStep, setTransferStep] = useState(0);
  const [transferFormData, setTransferFormData] = useState({
    adminName: "",
    adminTitle: "",
    email: "",
  });
  
  // API state management
  const [loading, setLoading] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [activeSchoolsCount, setActiveSchoolsCount] = useState(0);
  const [totalStudentsCount, setTotalStudentsCount] = useState(0);
  const [pendingSchoolsCount, setPendingSchoolsCount] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportingSchoolId, setExportingSchoolId] = useState(null);

  // Fetch schools data
  const fetchSchools = useCallback(async () => {
    setLoading(true);
    try {
      const response = await schoolService.getSchools({
        limit: itemsPerPage,
        page: currentPage,
        search: searchQuery || undefined,
        status: activeStatusFilter !== "all" ? activeStatusFilter : undefined,
        // Active tab = schools whose invited admin has accepted.
        hasAdmin: true,
      });

      const transformedSchools = response.data.map(school => ({
        school: school.name,
        logoUrl: school.logoUrl ?? null,
        adminName: school.schoolAdmin?.name ?? "",
        title: school.schoolAdmin?.title ?? "",
        email: school.schoolAdmin?.email ?? "",
        dateJoined: new Date(school.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        studentCount: school.studentCount?.toString() || "0",
        status: school.status.charAt(0).toUpperCase() + school.status.slice(1),
        id: school.id,
      }));
      
      setSchools(transformedSchools);
      setTotalPages(response.meta.totalPages);
      setTotalCount(response.meta.total);
    } catch (error) {
      console.error("Error fetching schools:", error);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, currentPage, searchQuery, activeStatusFilter]);

  // Pending tab = real School rows with no accepted admin yet
  // (hasAdmin=false). Server paginates, so no client-side slicing.
  const fetchPendingSchools = useCallback(async () => {
    setLoading(true);
    try {
      const response = await schoolService.getSchools({
        limit: itemsPerPage,
        page: currentPage,
        search: searchQuery || undefined,
        hasAdmin: false,
      });

      const transformed = response.data.map((school) => ({
        school: school.name,
        logoUrl: school.logoUrl ?? null,
        adminEmail: school.invitedAdminEmail || "",
        status: "Pending",
        inviteId: school.invitedAdminInviteId || null,
        inviteStatus: school.invitedAdminInviteStatus || null,
        dateAdded: new Date(school.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        studentCount: school.studentCount?.toString() || "0",
        id: school.id,
      }));

      setPendingSchools(transformed);
      setSelectedPendingIds([]);
      setTotalCount(response.meta.total);
      setTotalPages(response.meta.totalPages);
    } catch (error) {
      console.error("Error fetching pending schools:", error);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, currentPage, searchQuery]);

  // activeSchools/pendingSchools on the dashboard endpoint use the same
  // schoolAdminId predicate as the Active/Pending tabs (hasAdmin true/false),
  // so the cards stay in sync with the lists off a single call.
  const fetchStats = useCallback(async () => {
    try {
      const response = await schoolService.getAdminDashboardStats();
      setActiveSchoolsCount(response.activeSchools || 0);
      setPendingSchoolsCount(response.pendingSchools || 0);
      setTotalStudentsCount(response.totalStudents || 0);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      setActiveSchoolsCount(0);
      setPendingSchoolsCount(0);
      setTotalStudentsCount(0);
    }
  }, []);

  // Fetch data when tab changes or filters change
  useEffect(() => {
    if (activeTab === "Active Schools") {
      fetchSchools();
    } else {
      fetchPendingSchools();
    }
  }, [activeTab, fetchSchools, fetchPendingSchools]);

  // Fetch stats on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const isAnyModalOpen =
      showActivateModal ||
      showDeactivateModal ||
      showAddSchoolModal ||
      showInviteAdminModal ||
      showDetailModal ||
      transferStep > 0;

    if (isAnyModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [
    showActivateModal,
    showDeactivateModal,
    showAddSchoolModal,
    showInviteAdminModal,
    showDetailModal,
    transferStep,
  ]);

  const handleViewSchool = (school) => {
    setSelectedSchoolForDetail(school);
    setShowDetailModal(true);
  };

  const handleTransferAccount = () => {
    setShowDetailModal(false);
    setTransferStep(1);
  };

  const handleToggleStatus = (school) => {
    setSelectedStudent(school);
    if (school.status === "Inactive") {
      setShowActivateModal(true);
    } else {
      setShowDeactivateModal(true);
    }
  };

  const handleConfirmActivation = () => {
    if (selectedStudent) {
      setSchools((prev) =>
        prev.map((s) =>
          s.email === selectedStudent.email ? { ...s, status: "Active" } : s
        )
      );
    }
    setShowActivateModal(false);
    setSelectedStudent(null);
  };

  const handleConfirmDeactivation = async () => {
    if (selectedStudent) {
      try {
        setDeactivating(true);
        const response = await schoolService.deactivateSchool(selectedStudent.id);
        
        // Show success message
        toast.success(response.message || "School deactivated successfully");
        
        // Close modal first
        setShowDeactivateModal(false);
        setSelectedStudent(null);
        
        // Refresh the schools list after successful deactivation
        await fetchSchools();
      } catch (error) {
        console.error("Error deactivating school:", error);
        toast.error(error.message || "Failed to deactivate school. Please try again.");
      } finally {
        setDeactivating(false);
      }
    } else {
      setShowDeactivateModal(false);
      setSelectedStudent(null);
    }
  };

  // AddSchoolModal result. The modal now handles both "create school" and
  // "create school + send invite" in one go, depending on whether the
  // operator filled in the optional admin fields. `result.invited` tells
  // us which happened so we can toast accordingly; `inviteError` is set
  // when the school was created but the invite failed (school is kept,
  // operator retries the invite from the Pending tab).
  const handleSchoolChosen = async (school, result = {}) => {
    setShowAddSchoolModal(false);
    const name = school?.name || "school";
    // Optional logo-import suffix — only when the operator actually tried to
    // pull a logo from the URL. A logo failure never blocks the add (the
    // school is already saved), so it's a soft note, not a hard error.
    const logoSuffix = result.logoImported
      ? " — logo imported"
      : result.logoError
        ? " — couldn't import the logo, it can be uploaded later"
        : "";
    if (result.inviteError) {
      toast.warning(`${name} added, but invite failed: ${result.inviteError}`);
      setActiveTab("Pending Schools");
    } else if (result.invited) {
      toast.success(`${name} added and invite sent${logoSuffix}`);
      setActiveTab("Pending Schools");
    } else {
      toast.success(`${name} added${logoSuffix}`);
      setActiveTab("Pending Schools");
    }
    setCurrentPage(1);
    await Promise.all([fetchStats(), fetchPendingSchools()]);
    handleViewSchool({ ...school, school: school.name, status: "Pending" });
  };

  // PendingSchoolRow hands us a *transformed* row (`.school` is the name,
  // `.id` the school id, `.inviteId` the latest invite). Normalise into
  // the shape InviteSchoolAdminModal expects ({ id, name }) and stash the
  // invite id separately so the modal opens in View/Edit mode when there
  // is an existing invite to edit.
  const handleOpenInviteAdmin = (school) => {
    setSelectedSchoolForInvite({
      id: school.id,
      name: school.school || school.name || "",
    });
    setSelectedInviteId(school.inviteId || null);
    setShowInviteAdminModal(true);
  };

  const handleResendInvite = async (school) => {
    if (!school?.inviteId) return;
    try {
      await schoolService.resendInvite(school.inviteId);
      toast.success(`Invite resent for ${school.school}`);
      await Promise.all([fetchStats(), fetchPendingSchools()]);
    } catch (error) {
      toast.error(error.message || "Failed to resend invite");
    }
  };

  const handleCancelInvite = async (school) => {
    if (!school?.inviteId) return;
    if (
      !window.confirm(
        `Cancel the invite for ${school.school}? The recipient's link will stop working.`,
      )
    ) {
      return;
    }
    try {
      await schoolService.cancelInvite(school.inviteId);
      toast.success(`Invite cancelled for ${school.school}`);
      await Promise.all([fetchStats(), fetchPendingSchools()]);
    } catch (error) {
      toast.error(error.message || "Failed to cancel invite");
    }
  };

  const togglePendingSelect = (id) => {
    setSelectedPendingIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSelectAllPending = () => {
    setSelectedPendingIds((prev) =>
      prev.length === pendingSchools.length && pendingSchools.length > 0
        ? []
        : pendingSchools.map((s) => s.id),
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedPendingIds.length === 0) return;
    const n = selectedPendingIds.length;
    if (
      !window.confirm(
        `Delete ${n} selected school${n > 1 ? "s" : ""}? This cannot be undone. (Schools with an admin or students will be skipped.)`,
      )
    ) {
      return;
    }
    setDeletingSchools(true);
    try {
      const results = await Promise.allSettled(
        selectedPendingIds.map((id) => schoolService.deleteSchool(id)),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok > 0) toast.success(`Deleted ${ok} school${ok > 1 ? "s" : ""}`);
      if (failed > 0) {
        toast.error(
          `${failed} could not be deleted (they may have an admin or students)`,
        );
      }
      await Promise.all([fetchStats(), fetchPendingSchools()]);
    } catch (error) {
      toast.error(error.message || "Failed to delete schools");
    } finally {
      setDeletingSchools(false);
    }
  };

  // Step 2 success: an invite is out for this school. It stays in
  // Pending (no accepted admin yet) until the invitee accepts, at which
  // point the server sets schoolAdminId and it moves to Active.
  const handleAdminInvited = async (school) => {
    toast.success(`Invite sent for ${school?.name || "school"}`);
    setShowInviteAdminModal(false);
    setSelectedSchoolForInvite(null);
    setActiveTab("Pending Schools");
    setCurrentPage(1);
    await Promise.all([fetchStats(), fetchPendingSchools()]);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    if (activeTab === "Active Schools") {
      fetchSchools();
    } else {
      fetchPendingSchools();
    }
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleExport = async () => {
    if (activeTab !== "Active Schools") {
      toast.info("Export is available for Active Schools only.");
      return;
    }

    try {
      setExporting(true);
      const response = await schoolService.exportSchools({
        search: searchQuery || undefined,
        status: activeStatusFilter !== "all" ? activeStatusFilter : undefined,
      });

      const schoolsToExport = response.schools || [];
      if (schoolsToExport.length === 0) {
        toast.info("No schools found to export.");
        return;
      }

      const rows = schoolsToExport.map((school) => ({
        ID: school.id || "",
        "School Name": school.name || "",
        Status: school.status || "",
        "Created At": new Date(school.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        "Admin Name": school.schoolAdmin?.name || "",
        "Admin Email": school.schoolAdmin?.email || "",
        "Admin Title": school.schoolAdmin?.title || "",
        "Admin Status": school.schoolAdmin?.status || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Schools");
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `schools_export_${dateStamp}.xlsx`);
      toast.success(response.message || "Schools exported successfully");
    } catch (error) {
      console.error("Error exporting schools:", error);
      toast.error(error.message || "Failed to export schools");
    } finally {
      setExporting(false);
    }
  };

  const handleExportStudents = async (school) => {
    if (!school?.id) {
      toast.error("School ID is missing.");
      return;
    }

    try {
      setExportingSchoolId(school.id);
      const response = await schoolService.exportStudentsBySchool(school.id);

      const students = response.students || [];
      if (students.length === 0) {
        toast.info("No students found to export.");
        return;
      }

      // Human-facing columns only — the internal cuid ID and the always-empty
      // DOB (not returned by the export API) are dropped; Status is title-cased,
      // the public Hypepage link is built from the slug, and graduation shows
      // as a year. Headers are friendly and ordered name → contact → school.
      const rows = students.map((student) => ({
        "First Name": student.firstName || "",
        "Last Name": student.lastName || "",
        Email: student.email || "",
        Grade: student.grade || "",
        "Expected Graduation": student.expectedGraduationDate
          ? new Date(student.expectedGraduationDate).getFullYear()
          : "",
        Hypepage: student.slug && STUDENT_URL ? `${STUDENT_URL}/u/${student.slug}` : "",
        Status: titleCaseStatus(student.status),
        Joined: student.createdAt
          ? new Date(student.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      // Column widths (chars) so the sheet opens readable instead of cramped.
      worksheet["!cols"] = [
        { wch: 16 }, // First Name
        { wch: 16 }, // Last Name
        { wch: 30 }, // Email
        { wch: 22 }, // Grade
        { wch: 18 }, // Expected Graduation
        { wch: 42 }, // Hypepage
        { wch: 12 }, // Status
        { wch: 14 }, // Joined
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
      const dateStamp = new Date().toISOString().slice(0, 10);
      // Filename from the school name, not the opaque cuid.
      const safeName =
        (school.name || "students").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "").toLowerCase() || "students";
      XLSX.writeFile(workbook, `${safeName}_students_${dateStamp}.xlsx`);
      toast.success(response.message || "Students exported successfully");
    } catch (error) {
      console.error("Error exporting students:", error);
      toast.error(error.message || "Failed to export students");
    } finally {
      setExportingSchoolId(null);
    }
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const filteredSchools = useMemo(() => {
    if (activeStatusFilter === "all") return schools;
    return schools.filter(
      (school) => school.status.toLowerCase() === activeStatusFilter
    );
  }, [schools, activeStatusFilter]);

  const sortedSchools = useMemo(() => {
    const sortableSchools = [...filteredSchools];
    if (sortConfig.key !== null) {
      sortableSchools.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableSchools;
  }, [filteredSchools, sortConfig]);

  const sortedPendingSchools = useMemo(() => {
    const sortable = [...pendingSchools];
    if (sortConfig.key !== null) {
      sortable.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortable;
  }, [pendingSchools, sortConfig]);

  return (
    <div className="bg-white min-h-screen font-manrope pb-[100px]">
      <Sidebar />

      <div className="ml-[230px] mt-6 px-8 flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-0.5 font-['Manrope']">
            <h2 className="text-gray-900 text-2xl font-bold">Partner Schools</h2>
            <p className="text-gray-500 text-base font-normal">
              Formally onboarded schools and their invitation status. Candidate school names students typed during onboarding live under School Directory — a
              separate, larger list, so these counts won't match.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAddSchoolModal(true)}
            className="h-11 pl-3 pr-4 py-1.5 bg-primary hover:bg-blue-600 rounded-lg flex items-center gap-1.5 text-white font-medium transition-colors"
          >
            <Plus size={16} />
            Add School
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4 font-manrope">
          <StatCard
            icon={school}
            label="Active Schools"
            value={activeSchoolsCount.toString()}
            color="blue-500"
            onClick={() => {
              // Switch tabs + clear filters so the table that loads
              // matches the count the operator just clicked, then
              // scroll the table into view (the cards live above the
              // fold; without this the click feels like nothing happened).
              setActiveTab("Active Schools");
              setCurrentPage(1);
              setActiveStatusFilter("all");
              setSearchQuery("");
              tableSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          />
          <StatCard
            icon={group}
            label="Total Students"
            value={totalStudentsCount.toString()}
            color="primary"
            onClick={() => {
              navigate("/manage-users", { state: { roleFilter: "STUDENT" } });
            }}
          />
          <StatCard
            icon={Pending}
            label="Pending Schools"
            value={pendingSchoolsCount.toString()}
            color="blue-500"
            onClick={() => {
              setActiveTab("Pending Schools");
              setCurrentPage(1);
              setSearchQuery("");
              tableSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          />
        </div>

        <div ref={tableSectionRef} className="flex flex-col gap-6 mt-6 scroll-mt-6">
          <div className="border-b border-gray-200 flex gap-8">
            {TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => {
                  setActiveTab(tab);
                  setCurrentPage(1);
                  setSearchQuery("");
                }}
                className={`pb-4 text-lg font-semibold border-b-2 transition-all ${
                  activeTab === tab
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-900 hover:text-gray-600"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="flex justify-between items-center gap-4">
            <div className="w-96 h-12 bg-white rounded-lg outline outline-1 outline-blue-100 flex overflow-hidden">
              <input
                type="text"
                placeholder="Enter here..."
                className="flex-1 px-4 py-3 text-gray-500 font-medium outline-none"
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
              />
              <button 
                type="button"
                onClick={handleSearch}
                className="px-6 bg-secondary border-l border-blue-100 flex items-center gap-2 text-gray-500 font-medium hover:bg-blue-100 transition-colors"
              >
                <Search size={18} />
                Search
              </button>
            </div>

            <div className="flex items-center gap-3">
              {activeTab === "Pending Schools" && selectedPendingIds.length > 0 && (
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  disabled={deletingSchools}
                  className={`h-11 px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-white text-base font-medium transition-colors ${
                    deletingSchools
                      ? "bg-red-300 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {deletingSchools
                    ? "Deleting..."
                    : `Delete selected (${selectedPendingIds.length})`}
                </button>
              )}
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting || activeTab !== "Active Schools"}
                className={`h-11 px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-white text-base font-medium transition-colors ${
                  exporting || activeTab !== "Active Schools"
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-primary hover:bg-blue-600"
                }`}
              >
                <img src={Export} alt={"Export"} className="w-5 h-5" />
                {exporting ? "Exporting..." : "Export"}
              </button>

              {/* Status filter only applies to Active Schools
                  (ACTIVE/INACTIVE). Pending schools are uniformly
                  "awaiting admin" — no sub-states to filter. */}
              {activeTab === "Active Schools" && (
                <Dropdown
                  options={[
                    { label: "Select status", value: "all" },
                    { label: "Active", value: "active" },
                    { label: "Inactive", value: "inactive" },
                  ]}
                  value={activeStatusFilter}
                  onChange={setActiveStatusFilter}
                  placeholder="Select status"
                  width="w-48"
                />
              )}
            </div>
          </div>

          {loading ? (
            <div className="bg-white rounded-xl outline outline-1 outline-blue-100 overflow-hidden p-12 flex justify-center items-center">
              <div className="text-gray-500 text-lg">Loading...</div>
            </div>
          ) : activeTab === "Active Schools" ? (
            <div className="bg-white rounded-xl outline outline-1 outline-blue-100 overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-secondary">
                  <tr className="text-gray-900 text-xs font-semibold">
                    <th className="px-6 py-3 w-[13%]">
                      <SortButton
                        columnKey="school"
                        label="School Name"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[12%]">
                      <SortButton
                        columnKey="adminName"
                        label="Admin Name"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[10%]">
                      <SortButton
                        columnKey="title"
                        label="Title"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[18%]">
                      <SortButton
                        columnKey="email"
                        label="Email"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[12%]">
                      <SortButton
                        columnKey="dateJoined"
                        label="Date Joined"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[13%]">
                      <SortButton
                        columnKey="studentCount"
                        label="Student Count"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[10%]">
                      <SortButton
                        columnKey="status"
                        label="Status"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[12%]">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100">
                  {sortedSchools.map((school) => (
                    <SchoolRow
                      key={school.id || school.email}
                      school={school}
                      onToggleStatus={handleToggleStatus}
                      onViewSchool={handleViewSchool}
                      onExportStudents={handleExportStudents}
                      exporting={exportingSchoolId === school.id}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-xl outline outline-1 outline-blue-100 overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-secondary">
                  <tr className="text-gray-900 text-xs font-semibold">
                    <th className="px-6 py-3 w-[4%]">
                      <input
                        type="checkbox"
                        aria-label="Select all schools on this page"
                        checked={
                          pendingSchools.length > 0 &&
                          selectedPendingIds.length === pendingSchools.length
                        }
                        onChange={toggleSelectAllPending}
                        className="w-4 h-4 cursor-pointer accent-primary"
                      />
                    </th>
                    <th className="px-6 py-3 w-[18%]">
                      <SortButton
                        columnKey="school"
                        label="School Name"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[23%]">
                      <SortButton
                        columnKey="adminEmail"
                        label="Admin Email"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[15%]">
                      <SortButton
                        columnKey="dateAdded"
                        label="Date Added"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[12%]">
                      <SortButton
                        columnKey="studentCount"
                        label="Student Count"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[13%]">Status</th>
                    <th className="px-6 py-3 w-[15%]">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100">
                  {sortedPendingSchools.map((school) => (
                    <PendingSchoolRow
                      key={school.id}
                      school={school}
                      onViewSchool={handleViewSchool}
                      onResend={handleResendInvite}
                      onCancel={handleCancelInvite}
                      selected={selectedPendingIds.includes(school.id)}
                      onToggleSelect={togglePendingSelect}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-between items-center font-['Manrope']">
            <div className="flex items-center gap-2">
              <span className="text-gray-900 text-sm">Results per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-20 h-10 rounded-full outline outline-1 outline-blue-100 px-4 text-gray-500 text-sm cursor-pointer hover:bg-blue-50 transition-colors"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-2 bg-white rounded-full outline outline-1 outline-blue-100 p-1">
              <button 
                type="button"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="flex items-center">
                {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (currentPage <= 3) {
                    pageNum = idx + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = currentPage - 2 + idx;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 flex items-center justify-center text-sm font-medium rounded-full ${
                        currentPage === pageNum
                          ? "bg-blue-100 text-gray-900"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button 
                type="button"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <span className="text-gray-900 text-sm font-normal">
              Showing {totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} results
            </span>
          </div>
        </div>
      </div>

      {showActivateModal && (
        <ActivateSchoolModal
          onClose={() => {
            setShowActivateModal(false);
            setSelectedStudent(null);
          }}
          onConfirm={handleConfirmActivation}
        />
      )}

      {showDeactivateModal && (
        <DeactivateSchoolModal
          onClose={() => {
            setShowDeactivateModal(false);
            setSelectedStudent(null);
          }}
          onConfirm={handleConfirmDeactivation}
          isLoading={deactivating}
        />
      )}

      {showAddSchoolModal && (
        <AddSchoolModal
          onClose={() => setShowAddSchoolModal(false)}
          onConfirm={handleSchoolChosen}
        />
      )}

      {showInviteAdminModal && selectedSchoolForInvite && (
        <InviteSchoolAdminModal
          school={selectedSchoolForInvite}
          inviteId={selectedInviteId}
          onClose={() => {
            setShowInviteAdminModal(false);
            setSelectedSchoolForInvite(null);
            setSelectedInviteId(null);
          }}
          onConfirm={handleAdminInvited}
        />
      )}

      {showDetailModal && (
        <DetailSchoolModal
          onClose={() => { setShowDetailModal(false); void Promise.all([fetchStats(), fetchPendingSchools()]); }}
          school={selectedSchoolForDetail}
          onDeactivate={() => {
            setShowDetailModal(false);
            handleToggleStatus(selectedSchoolForDetail);
          }}
          onTransfer={handleTransferAccount}
          onExport={() => handleExportStudents(selectedSchoolForDetail)}
          exporting={exportingSchoolId === selectedSchoolForDetail?.id}
        />
      )}

      {transferStep === 1 && (
        <TransferOwnershipModal1
          onClose={() => {
            setTransferStep(0);
            setTransferFormData({ adminName: "", adminTitle: "", email: "" });
          }}
          onContinue={() => setTransferStep(2)}
        />
      )}

      {transferStep === 2 && (
        <TransferOwnershipModal2
          onClose={() => {
            setTransferStep(0);
            setTransferFormData({ adminName: "", adminTitle: "", email: "" });
          }}
          onBack={() => setTransferStep(1)}
          onContinue={(data) => {
            setTransferFormData(data);
            setTransferStep(3);
          }}
        />
      )}

      {transferStep === 3 && (
        <TransferOwnershipModal3
          onClose={() => {
            setTransferStep(0);
            setTransferFormData({ adminName: "", adminTitle: "", email: "" });
          }}
          onBack={() => setTransferStep(2)}
          onContinue={() => setTransferStep(4)}
          formData={transferFormData}
          currentEmail={selectedSchoolForDetail?.email || ""}
        />
      )}

      {transferStep === 4 && (
        <TransferOwnershipModal4
          onClose={() => {
            setTransferStep(0);
            setTransferFormData({ adminName: "", adminTitle: "", email: "" });
          }}
          newAdminEmail={transferFormData.email}
        />
      )}
    </div>
  );
};

export default ManageSchools;
